/**
 * `?sla=` — S-14 with a verification timeframe, and without one.
 *
 * ```
 * ?sla=on    → orders carry estimatedVerificationBy; S-14 states a timeframe
 * ?sla=off   → the default: the field is null and S-14 says it cannot say
 * ```
 *
 * The prototype draws S-14 twice, once with "Estimare: 1–2 zile lucrătoare" and once
 * with "Îți scriem imediat ce verificarea s-a încheiat". No source chooses between
 * them, because no source contains an SLA for manual contract review at all (Q-19).
 *
 * So the screen is not built with a display flag. It reads `estimatedVerificationBy`
 * off the wire and renders whichever reading the data supports — which is the shape
 * the real answer will arrive in: a duration, or nothing. This switch only decides
 * whether the field is populated, and **off is the default**, because the absence is
 * the current state of the specification.
 *
 * Why it exists at all: a reading nobody can see is a reading nobody will comment on.
 * The with-timeframe version needs to be walkable so Orange can look at it and say
 * what the number should be, or that there isn't one.
 *
 * Held in `sessionStorage` for the same reason as `?rate=` — the query string does not
 * survive the first in-app navigation, and an order screen is reached by placing an
 * order rather than by typing a URL. It dies with the tab. The dev panel is now a
 * second caller — it pairs `setSlaSwitch` with `applySlaSwitch`, which takes effect
 * from the next order placed — and the URL form stays, because the Playwright specs
 * drive forced states by URL rather than by clicking a panel.
 *
 * Note what "without a reload" can and cannot mean here: the field is stamped onto an
 * order when the order is created, so flipping the switch does not retrofit an estimate
 * onto an order that already exists. That is a property of the data, not a limitation of
 * the panel, and the panel says so next to the toggle.
 *
 * POC scaffolding. Nothing in production has a switch for whether it makes a promise.
 */
import { inject, provideAppInitializer } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import type { DemoControls } from '../../mock-bff/mock-backend';
import { MockBackend } from '../../mock-bff/mock-backend';

export type SlaSwitch = 'on' | 'off';

const STORAGE_KEY = 'pre2post.poc.sla.v1';

/** Applies a held or URL-supplied switch at startup. */
export function provideVerificationEstimateSwitch(): EnvironmentProviders {
  return provideAppInitializer(() => {
    // Only ever switched on from here. `off` is what `poc-config.ts` already says.
    if (read() !== 'on') return;
    applySlaSwitch(inject(MockBackend).controls, 'on');
  });
}

/** Holds the choice for the rest of the tab's life. Persists only; see the header. */
export function setSlaSwitch(value: SlaSwitch): void {
  try {
    if (value === 'off') sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private browsing. The switch applies to this page load and no further.
  }
}

export function clearSlaSwitch(): void {
  setSlaSwitch('off');
}

/**
 * The effect, separated from the persistence so the panel can apply it without a
 * reload. Takes `DemoControls` so it is callable outside an injection context.
 */
export function applySlaSwitch(controls: DemoControls, value: SlaSwitch): void {
  controls.setVerificationEstimate(value === 'on');
}

/** What is held right now. The panel's display value; `off` is the default. */
export function currentSlaSwitch(): SlaSwitch {
  try {
    return parseSlaSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '') ?? 'off';
  } catch {
    return 'off';
  }
}

/** The URL wins over the held value, and replaces it. */
function read(): SlaSwitch | null {
  try {
    const fromUrl = new URLSearchParams(location.search).get('sla');
    if (fromUrl !== null) {
      const parsed = parseSlaSwitch(fromUrl);
      if (parsed !== null) {
        setSlaSwitch(parsed);
        return parsed;
      }
    }
    return parseSlaSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return null;
  }
}

export function parseSlaSwitch(value: string): SlaSwitch | null {
  return value === 'on' || value === 'off' ? value : null;
}
